/**
 * CountingTrainer - Interactive Hand Counting Practice & Scoring Trainer
 * Allows players to:
 * - Practice counting 6-card hands (discard 2, keep 4) or 9-card hands (discard 3, keep 6)
 * - Cut a random starter card
 * - Fill out their point counts for:
 *   15's, Pairs, Runs, Flush, and His Nobs
 * - Live update the total score at the bottom
 * - Check scores with instant green checkmarks (✔) or red X's (✖) per category
 * - Track hands correct, hands wrong, and accuracy percentage
 * - Enter custom hands or card combinations
 * - Share counting challenges via URL and text
 * - Inspect full score details with highlighted cards for each point
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { Hand } from '../models/Hand.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { SUIT_SYM, RANK_NAME } from '../scoring/ScoringConstants.js';
import { Profile } from '../profile/Profile.js';
import { DiscardTrainer } from './DiscardTrainer.js';

export class CountingTrainer {
  /**
   * In-memory session statistics.
   */
  static stats = {
    handsPlayed: 0,
    handsCorrect: 0,
    handsWrong: 0,
    currentStreak: 0,
    bestStreak: 0,
    accuracyPct: 0
  };

  /**
   * Resets in-session statistics and optionally career stats.
   * @param {Object} [options]
   */
  static resetStats(options = {}) {
    CountingTrainer.stats = {
      handsPlayed: 0,
      handsCorrect: 0,
      handsWrong: 0,
      currentStreak: 0,
      bestStreak: 0,
      accuracyPct: 0
    };
    if (options && options.career) {
      try {
        const prof = Profile.getInstance(options.storage);
        if (prof && typeof prof.clearCountingTrainerStats === 'function') {
          prof.clearCountingTrainerStats(options.storage);
        }
      } catch (_) {}
    }
  }

  /**
   * Returns current session and career statistics.
   * @param {Object} [storage=null]
   * @returns {{ session: Object, career: Object }}
   */
  static getStats(storage = null) {
    let career = {
      handsPlayed: 0,
      handsCorrect: 0,
      handsWrong: 0,
      currentStreak: 0,
      bestStreak: 0,
      accuracyPct: 0
    };
    try {
      const prof = Profile.getInstance(storage);
      if (prof && typeof prof.getCountingTrainerStats === 'function') {
        career = prof.getCountingTrainerStats(storage);
      }
    } catch (_) {}

    return {
      session: {
        ...CountingTrainer.stats,
        accuracyPct: CountingTrainer.stats.handsPlayed > 0
          ? Math.round((CountingTrainer.stats.handsCorrect / CountingTrainer.stats.handsPlayed) * 100)
          : 0
      },
      career
    };
  }

  /**
   * Returns career stats from Profile or localStorage.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getCareerStats(storage = null) {
    return CountingTrainer.getStats(storage).career;
  }

  /**
   * Sorts cards lowest to highest by rank (Ace=1 to King=13), then by suit.
   * @param {Card[]} [cards=[]]
   * @returns {Card[]}
   */
  static sortCards(cards = []) {
    if (Hand && typeof Hand.sort === 'function') {
      return Hand.sort(cards);
    }
    return (cards || []).slice().sort((a, b) => {
      const ar = (a && (a.rank !== undefined ? a.rank : (a.r !== undefined ? a.r : 0))) || 0;
      const br = (b && (b.rank !== undefined ? b.rank : (b.r !== undefined ? b.r : 0))) || 0;
      const as = String((a && (a.suit || a.s || a.rawSuit)) || 'S').toUpperCase();
      const bs = String((b && (b.suit || b.s || b.rawSuit)) || 'S').toUpperCase();
      const suitsOrder = ['S', 'H', 'D', 'C'];
      const aSuitIdx = suitsOrder.indexOf(as) !== -1 ? suitsOrder.indexOf(as) : 99;
      const bSuitIdx = suitsOrder.indexOf(bs) !== -1 ? suitsOrder.indexOf(bs) : 99;
      return (ar - br) || (aSuitIdx - bSuitIdx);
    });
  }

  /**
   * Deals a random hand of 6 or 9 cards from a fresh 52-card deck, sorted lowest to highest.
   * @param {number} [mode=6] Hand size (6 for standard, 9 for super)
   * @returns {Card[]}
   */
  static dealRandomHand(mode = 6) {
    const handSize = mode === 9 ? 9 : 6;
    const deck = new Deck();
    deck.shuffle();
    const dealt = deck.deal(handSize);
    return CountingTrainer.sortCards(dealt);
  }

  /**
   * Cuts a random starter card from the remaining deck.
   * @param {Card[]} [excludedCards=[]] Cards already dealt/kept/discarded
   * @returns {Card}
   */
  static cutStarterCard(excludedCards = []) {
    const excludedIds = new Set(excludedCards.map(c => c.id || `${c.r}${c.s}`));
    const deck = new Deck();
    deck.shuffle();
    for (const card of deck.cards) {
      const id = card.id || `${card.r}${card.s}`;
      if (!excludedIds.has(id)) {
        return card;
      }
    }
    return deck.cards[0] || new Card({ r: 5, s: 'H' });
  }

  /**
   * Fully scores a kept hand and starter card using core Cribbage scoring rules.
   * @param {Card[]} cards Kept cards in hand (4 for 6-card deal, 6 for 9-card deal)
   * @param {Card} starter Starter cut card
   * @param {boolean} [isCrib=false] Whether counting as a crib
   * @param {Object} [options={}] Optional variant settings
   * @returns {Object} Structured score breakdown
   */
  static scoreCountedHand(cards, starter, isCrib = false, options = {}) {
    const rawCards = (cards || []).map(c => (c instanceof Card ? c : Card.from(c)));
    const rawStarter = starter instanceof Card ? starter : (starter ? Card.from(starter) : null);

    const scored = CribbageScorer.scoreHandCore(rawCards, rawStarter, isCrib, options);

    return {
      fifteens: scored.fifteens || 0,
      pairs: scored.pairs || 0,
      runs: scored.runs || 0,
      flush: scored.flush || 0,
      nobs: scored.nobs || 0,
      straight: scored.straight || 0,
      fullHouse: scored.fullHouse || 0,
      straightFlush: scored.straightFlush || 0,
      royalFlush: scored.royalFlush || 0,
      total: scored.total || 0,
      notes: scored.notes || [],
      lines: scored.lines || []
    };
  }

  /**
   * Evaluates the player's entered points against the actual score breakdown.
   * @param {Object} actualBreakdown Return value from scoreCountedHand
   * @param {Object} userInputs User-entered values { fifteens, pairs, runs, flush, nobs, total }
   * @returns {Object} Evaluation with per-category pass/fail and actual values
   */
  static evaluateUserCount(actualBreakdown, userInputs = {}) {
    const userFifteens = Number(userInputs.fifteens) || 0;
    const userPairs = Number(userInputs.pairs) || 0;
    const userRuns = Number(userInputs.runs) || 0;
    const userFlush = Number(userInputs.flush) || 0;
    const userNobs = Number(userInputs.nobs) || 0;

    const actualFifteens = actualBreakdown.fifteens || 0;
    const actualPairs = actualBreakdown.pairs || 0;
    const actualRuns = actualBreakdown.runs || 0;
    const actualFlush = actualBreakdown.flush || 0;
    const actualNobs = actualBreakdown.nobs || 0;
    const actualTotal = actualBreakdown.total || 0;

    // Computed user sum or explicit user total
    const computedUserTotal = userFifteens + userPairs + userRuns + userFlush + userNobs;
    const userTotal = userInputs.total !== undefined ? Number(userInputs.total) || 0 : computedUserTotal;

    const res = {
      fifteens: {
        user: userFifteens,
        actual: actualFifteens,
        correct: userFifteens === actualFifteens
      },
      pairs: {
        user: userPairs,
        actual: actualPairs,
        correct: userPairs === actualPairs
      },
      runs: {
        user: userRuns,
        actual: actualRuns,
        correct: userRuns === actualRuns
      },
      flush: {
        user: userFlush,
        actual: actualFlush,
        correct: userFlush === actualFlush
      },
      nobs: {
        user: userNobs,
        actual: actualNobs,
        correct: userNobs === actualNobs
      },
      total: {
        user: userTotal,
        actual: actualTotal,
        correct: userTotal === actualTotal
      }
    };

    res.isAllCorrect = res.fifteens.correct &&
                       res.pairs.correct &&
                       res.runs.correct &&
                       res.flush.correct &&
                       res.nobs.correct &&
                       res.total.correct;

    return res;
  }

  /**
   * Analyzes dealt hand and evaluates the player's discard choices against EV table.
   * @param {Card[]} dealtHand Full dealt hand (6 or 9 cards)
   * @param {Card[]} userDiscards Cards the player discarded (2 or 3 cards)
   * @param {boolean} [isDealer=true]
   * @returns {{ analysis: Object, evaluation: Object, isCorrect: boolean }|null}
   */
  static analyzeDiscards(dealtHand, userDiscards, isDealer = true) {
    if (!dealtHand || (dealtHand.length !== 6 && dealtHand.length !== 9)) return null;
    const rawDealt = dealtHand.map(c => (c instanceof Card ? c : Card.from(c)));
    const rawDisc = (userDiscards || []).map(c => (c instanceof Card ? c : Card.from(c)));
    const analysis = DiscardTrainer.analyzeHand(rawDealt, isDealer);
    const evaluation = DiscardTrainer.evaluateUserChoice(analysis, rawDisc, false);
    return {
      analysis,
      evaluation,
      isCorrect: evaluation ? evaluation.isOptimal : false
    };
  }

  /**
   * Records a completed counting hand into session and profile career stats.
   * @param {Object} playData
   * @param {boolean} playData.isCorrect Whether all categories and total were 100% correct
   * @param {Object} [storage=null]
   * @returns {Object} Updated career statistics
   */
  static recordPlay(playData = {}, storage = null) {
    const isCorrect = Boolean(playData.isCorrect);

    CountingTrainer.stats.handsPlayed++;
    if (isCorrect) {
      CountingTrainer.stats.handsCorrect++;
      CountingTrainer.stats.currentStreak++;
      CountingTrainer.stats.bestStreak = Math.max(
        CountingTrainer.stats.bestStreak,
        CountingTrainer.stats.currentStreak
      );
    } else {
      CountingTrainer.stats.handsWrong++;
      CountingTrainer.stats.currentStreak = 0;
    }

    CountingTrainer.stats.accuracyPct = Math.round(
      (CountingTrainer.stats.handsCorrect / CountingTrainer.stats.handsPlayed) * 100
    );

    let careerStats = null;
    try {
      const prof = Profile.getInstance(storage);
      if (prof && typeof prof.recordCountingTrainerPlay === 'function') {
        careerStats = prof.recordCountingTrainerPlay({ isCorrect }, storage);
      }
    } catch (_) {}

    return careerStats || CountingTrainer.stats;
  }

  /**
   * Parses custom card input string or array into Card instances.
   * Supports:
   * - 5 cards (4 hand cards + 1 cut card as the last card): "5H 5D 5S JC 5C" or "5H 5D 5S JC + 5C"
   * - 7 cards (6 hand cards + 1 cut card as the last card): "5H 5D 5S JC 7H 8D 5C" or "5H 5D 5S JC 7H 8D + 5C"
   * - Dealt hand fallback (6 or 9 cards): "5H 5D 5S JC 7H 8D"
   * @param {string|Array} input
   * @param {number} [mode=6]
   * @returns {{ cards: Card[], starter: Card|null, mode: number, isComplete: boolean }|null}
   */
  static parseCustomHand(input, mode = 6) {
    if (!input) return null;

    let text = '';
    if (Array.isArray(input)) {
      text = input.map(c => (c && (c.id || `${c.r || c.rank}${c.s || c.suit}`))).join(' ');
    } else {
      text = String(input);
    }

    let starterPart = null;
    let handPart = text;

    if (text.includes('+')) {
      const parts = text.split('+');
      handPart = parts[0].trim();
      starterPart = parts.slice(1).join('+').trim();
    } else if (text.includes('|')) {
      const parts = text.split('|');
      handPart = parts[0].trim();
      starterPart = parts.slice(1).join('|').trim();
    }

    const rankMap = { A: 1, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, T: 10, J: 11, Q: 12, K: 13 };
    const suitMap = { S: 'S', H: 'H', D: 'D', C: 'C', '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' };
    const parseToken = (t) => {
      const cleaned = String(t).trim().toUpperCase();
      const m = cleaned.match(/^([2-9]|10|[TJQKA])([SHDC♠♥♦♣])$/);
      if (!m) return null;
      const rank = rankMap[m[1]];
      const suit = suitMap[m[2]];
      const rName = (RANK_NAME && RANK_NAME[rank]) || (rank === 1 ? 'A' : rank === 11 ? 'J' : rank === 12 ? 'Q' : rank === 13 ? 'K' : String(rank));
      return new Card({ r: rank, s: suit, id: `${rName}${suit}` });
    };

    const handTokens = handPart.split(/[\s,;]+/).filter(Boolean);
    const parsedHand = [];
    const seen = new Set();

    for (const t of handTokens) {
      const card = parseToken(t);
      if (!card || seen.has(card.id)) return null;
      seen.add(card.id);
      parsedHand.push(card);
    }

    let parsedStarter = null;
    if (starterPart) {
      const starterTokens = starterPart.split(/[\s,;]+/).filter(Boolean);
      if (starterTokens.length > 0) {
        parsedStarter = parseToken(starterTokens[0]);
        if (!parsedStarter || seen.has(parsedStarter.id)) return null;
        seen.add(parsedStarter.id);
      }
    }

    // Explicit starter provided via '+' or '|'
    if (parsedStarter) {
      if (parsedHand.length === 4) {
        return {
          cards: CountingTrainer.sortCards(parsedHand),
          starter: parsedStarter,
          mode: 6,
          isComplete: true
        };
      }
      if (parsedHand.length === 6) {
        return {
          cards: CountingTrainer.sortCards(parsedHand),
          starter: parsedStarter,
          mode: 9,
          isComplete: true
        };
      }
      return null;
    }

    // 5 cards: 4 in hand + last (5th) is starter cut card
    if (parsedHand.length === 5) {
      return {
        cards: CountingTrainer.sortCards(parsedHand.slice(0, 4)),
        starter: parsedHand[4],
        mode: 6,
        isComplete: true
      };
    }

    // 7 cards: 6 in hand + last (7th) is starter cut card
    if (parsedHand.length === 7) {
      return {
        cards: CountingTrainer.sortCards(parsedHand.slice(0, 6)),
        starter: parsedHand[6],
        mode: 9,
        isComplete: true
      };
    }

    // Legacy fallback: 6 or 9 dealt cards without starter
    if (parsedHand.length === 6 && !parsedStarter) {
      return {
        cards: CountingTrainer.sortCards(parsedHand),
        starter: null,
        mode: 6,
        isComplete: false
      };
    }
    if (parsedHand.length === 9 && !parsedStarter) {
      return {
        cards: CountingTrainer.sortCards(parsedHand),
        starter: null,
        mode: 9,
        isComplete: false
      };
    }

    return null;
  }

  /**
   * Encodes a counting challenge into a shareable URL query string.
   * @param {Card[]} cards Kept or dealt cards
   * @param {Card} starter Starter card
   * @param {number} [mode=6]
   * @returns {string} URL string
   */
  static encodeShareUrl(cards, starter, mode = 6) {
    const cardCodes = (cards || []).map(c => {
      const r = (c.rankName && c.rankName !== '?') ? c.rankName : (c.rank === 1 ? 'A' : c.rank === 11 ? 'J' : c.rank === 12 ? 'Q' : c.rank === 13 ? 'K' : String(c.rank || c.r || ''));
      const s = c.suit || c.s || 'S';
      return `${r}${s}`;
    }).join(',');
    const starterCode = starter ? (() => {
      const r = (starter.rankName && starter.rankName !== '?') ? starter.rankName : (starter.rank === 1 ? 'A' : starter.rank === 11 ? 'J' : starter.rank === 12 ? 'Q' : starter.rank === 13 ? 'K' : String(starter.rank || starter.r || ''));
      const s = starter.suit || starter.s || 'S';
      return `${r}${s}`;
    })() : '';
    const params = new URLSearchParams({
      trainer: 'counting',
      mode: String(mode),
      cards: cardCodes,
      starter: starterCode
    });

    let base = 'https://crazycribbage.com';
    if (typeof window !== 'undefined' && window.location) {
      if (window.location.href && !window.location.href.startsWith('about:')) {
        base = window.location.href.split('?')[0].split('#')[0];
      } else if (window.location.origin && window.location.origin !== 'null') {
        base = window.location.origin + window.location.pathname;
      }
    }
    return `${base}?${params.toString()}`;
  }

  /**
   * Decodes query parameters from a URL or search string.
   * @param {string} [searchStr]
   * @returns {{ cards: Card[], starter: Card, mode: number }|null}
   */
  static decodeShareUrl(searchStr) {
    try {
      const search = searchStr || (typeof window !== 'undefined' ? window.location.search : '');
      if (!search) return null;
      const params = new URLSearchParams(search);
      if (params.get('trainer') !== 'counting' && params.get('tool') !== 'counting' && params.get('mode') !== 'counting') return null;

      const mode = params.get('mode') === '9' ? 9 : 6;
      const cardsParam = params.get('cards');
      const starterParam = params.get('starter');

      if (!cardsParam) return null;

      const rankMap = { A: 1, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, T: 10, J: 11, Q: 12, K: 13 };
      const parseToken = (t) => {
        const m = String(t).trim().toUpperCase().match(/^([2-9]|10|[TJQKA]|1)([SHDC])$/);
        if (!m) return null;
        const rank = rankMap[m[1]];
        const suit = m[2];
        const rName = (RANK_NAME && RANK_NAME[rank]) || (rank === 1 ? 'A' : rank === 11 ? 'J' : rank === 12 ? 'Q' : rank === 13 ? 'K' : String(rank));
        return new Card({ r: rank, s: suit, id: `${rName}${suit}` });
      };

      const cards = cardsParam.split(',').map(parseToken).filter(Boolean);
      const starter = starterParam ? parseToken(starterParam) : null;

      if (!cards.length) return null;

      return {
        cards: CountingTrainer.sortCards(cards),
        kept: starter ? CountingTrainer.sortCards(cards) : null,
        hand: !starter ? CountingTrainer.sortCards(cards) : null,
        starter,
        mode
      };
    } catch (_) {
      return null;
    }
  }

  /**
   * Generates a rich text challenge for social sharing or copying.
   * @param {Card[]} cards
   * @param {Card} starter
   * @param {number} [mode=6]
   * @returns {string}
   */
  static generateShareText(cards, starter, mode = 6) {
    const suitSym = { S: '♠', H: '♥', D: '♦', C: '♣' };
    const cardLabels = (cards || []).map(c => {
      const r = (c && c.rankName && c.rankName !== '?') ? c.rankName : (c && c.rank === 1 ? 'A' : c && c.rank === 11 ? 'J' : c && c.rank === 12 ? 'Q' : c && c.rank === 13 ? 'K' : String((c && (c.rank || c.r)) || ''));
      const su = String((c && (c.suit || c.s)) || '').toUpperCase();
      const s = (SUIT_SYM && SUIT_SYM[su]) || suitSym[su] || su;
      return `${r}${s}`;
    }).join(' ');

    let starterLabel = 'None';
    if (starter) {
      const r = (starter.rankName && starter.rankName !== '?') ? starter.rankName : (starter.rank === 1 ? 'A' : starter.rank === 11 ? 'J' : starter.rank === 12 ? 'Q' : starter.rank === 13 ? 'K' : String(starter.rank || starter.r || ''));
      const su = String(starter.suit || starter.s || '').toUpperCase();
      const s = (SUIT_SYM && SUIT_SYM[su]) || suitSym[su] || su;
      starterLabel = `${r}${s}`;
    }

    const shareUrl = CountingTrainer.encodeShareUrl(cards, starter, mode);

    return `🧮 Crazy Cribbage - Counting Challenge!\nMode: ${mode === 9 ? '9-Card Super Hand' : 'Standard 6-Card Hand'}\nHand: ${cardLabels} | Starter: ${starterLabel}\nCan you score this hand correctly without peeking?\nTest yourself here: ${shareUrl}`;
  }
}
